import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuthShell } from "@/pages/Login";
import { Loader2, MailCheck, CheckCircle2 } from "lucide-react";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <AuthShell title="Check your inbox" subtitle="We've sent you a reset link if that email is registered.">
        <div className="rounded-2xl border border-sage/40 bg-sage/10 p-6 text-center" data-testid="forgot-sent">
          <MailCheck className="mx-auto mb-3 h-9 w-9 text-sage" strokeWidth={1.5} />
          <p className="text-sm text-cocoa/80">If an account exists for <span className="font-medium text-cocoa">{email}</span>, a password reset link is on its way. The link expires in 60 minutes.</p>
        </div>
        <p className="mt-6 text-center text-sm text-cocoa/60">
          <Link to="/login" className="font-medium text-terracotta hover:underline" data-testid="forgot-back-login">Back to sign in</Link>
        </p>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Forgot your password?" subtitle="Enter your email and we'll send you a link to reset it.">
      <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
        <div>
          <Label htmlFor="email" className="text-cocoa">Email</Label>
          <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5 rounded-xl bg-warmwhite" data-testid="forgot-email" />
        </div>
        {error && <p className="text-sm text-destructive" data-testid="forgot-error">{error}</p>}
        <Button type="submit" disabled={loading} className="w-full rounded-full bg-terracotta py-6 text-warmwhite hover:bg-terracotta/90" data-testid="forgot-submit">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send reset link"}
        </Button>
      </form>
      <p className="mt-6 text-center text-sm text-cocoa/60">
        Remembered it? <Link to="/login" className="font-medium text-terracotta hover:underline" data-testid="forgot-to-login">Sign in</Link>
      </p>
    </AuthShell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) return setError("Password must be at least 6 characters.");
    if (password !== confirm) return setError("Passwords don't match.");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      await refresh();
      setTimeout(() => navigate("/dashboard"), 1200);
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || "Could not reset password");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <AuthShell title="Invalid link" subtitle="This password reset link is missing or malformed.">
        <p className="text-center text-sm text-cocoa/60">
          <Link to="/forgot-password" className="font-medium text-terracotta hover:underline" data-testid="reset-request-new">Request a new reset link</Link>
        </p>
      </AuthShell>
    );
  }

  if (done) {
    return (
      <AuthShell title="Password updated" subtitle="You're all set.">
        <div className="rounded-2xl border border-sage/40 bg-sage/10 p-6 text-center" data-testid="reset-done">
          <CheckCircle2 className="mx-auto mb-3 h-9 w-9 text-sage" strokeWidth={1.5} />
          <p className="text-sm text-cocoa/80">Your password has been reset. Taking you to your dashboard…</p>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Choose a new password" subtitle="Enter and confirm your new password below.">
      <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
        <div>
          <Label htmlFor="password" className="text-cocoa">New password</Label>
          <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="mt-1.5 rounded-xl bg-warmwhite" data-testid="reset-password" />
          <p className="mt-1 text-xs text-cocoa/55">At least 6 characters.</p>
        </div>
        <div>
          <Label htmlFor="confirm" className="text-cocoa">Confirm new password</Label>
          <Input id="confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={6} className="mt-1.5 rounded-xl bg-warmwhite" data-testid="reset-confirm" />
        </div>
        {error && <p className="text-sm text-destructive" data-testid="reset-error">{error}</p>}
        <Button type="submit" disabled={loading} className="w-full rounded-full bg-terracotta py-6 text-warmwhite hover:bg-terracotta/90" data-testid="reset-submit">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reset password"}
        </Button>
      </form>
      <p className="mt-6 text-center text-sm text-cocoa/60">
        <Link to="/login" className="font-medium text-terracotta hover:underline" data-testid="reset-to-login">Back to sign in</Link>
      </p>
    </AuthShell>
  );
}
